import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./Hero.css";
import { CmsText } from "../../../RichTextContent/RichTextContent";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { useHomeSection } from "../../../../context/HomePageContext";
import { resolveMediaUrl } from "../../../../utils/resolveMediaUrl";
import {
  isVideoUrl,
  videoMimeFromUrl,
} from "../../../../utils/mediaPaths";

function HeroBackdrop({ src }) {
  if (!src) return null;
  if (isVideoUrl(src)) {
    return (
      <video
        key={src}
        className="hero-video"
        autoPlay
        loop
        muted
        playsInline
      >
        <source src={src} type={videoMimeFromUrl(src)} />
      </video>
    );
  }
  return (
    <img
      key={src}
      className="hero-image"
      src={src}
      alt=""
      decoding="async"
    />
  );
}

function collectHeroSources(section) {
  if (!section?.media) return [];
  const urls = [];
  const primary = section.media.primary?.url;
  if (primary) urls.push(resolveMediaUrl(primary));
  (section.media.videos || []).forEach((item) => {
    if (item?.url) urls.push(resolveMediaUrl(item.url));
  });
  (section.media.groups?.background || []).forEach((item) => {
    if (item?.url) urls.push(resolveMediaUrl(item.url));
  });
  return [...new Set(urls)];
}

const Hero = () => {
  const { section, loading } = useHomeSection("hero");
  const [activeIndex, setActiveIndex] = useState(0);

  const sources = useMemo(() => collectHeroSources(section), [section]);
  const sourcesKey = sources.join("|");

  useEffect(() => {
    setActiveIndex(0);
  }, [sourcesKey]);

  if (loading || !section?.fields) return null;

  const fields = section.fields;
  const count = sources.length;
  const index =
    count > 0 ? ((activeIndex % count) + count) % count : 0;
  const currentSrc = count > 0 ? sources[index] : "";
  const showArrowControls = count > 1;

  const goPrev = () => {
    if (count < 2) return;
    setActiveIndex((i) => (i - 1 + count) % count);
  };

  const goNext = () => {
    if (count < 2) return;
    setActiveIndex((i) => (i + 1) % count);
  };

  return (
    <section className="hero">
      {currentSrc && <HeroBackdrop src={currentSrc} />}

      <div className="hero-overlay" />

      <div className="hero-content">
        {fields.title ? (
          <CmsText content={fields.title} as="h1" />
        ) : null}

        {fields.subtitle ? (
          <CmsText content={fields.subtitle} as="p" />
        ) : null}

        <Link to={fields.buttonLink} className="hero-btn buttonclip">
          <CmsText content={fields.buttonText} as="span" />
        </Link>

        {showArrowControls && (
          <div className="hero-controls">
            <button
              type="button"
              className="arrow-btn"
              onClick={goPrev}
              aria-label="Previous slide"
            >
              <IoChevronBack className="arrow-btn__icon" />
            </button>
            <button
              type="button"
              className="arrow-btn"
              onClick={goNext}
              aria-label="Next slide"
            >
              <IoChevronForward className="arrow-btn__icon" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
